#!/usr/bin/env node
import { Command } from "commander";
import { addConfigCommands } from "@crunchdao/shared-cli";
import { RegisterCommand } from "./commands/register";
import { GetCommand } from "./commands/get";
import { GetConfigCommand } from "./commands/get-config";
import { CreateCommand } from "./commands/create";
import { StartCommand } from "./commands/start";
import { GetCrunchCommand } from "./commands/get-crunch";
import { GetCruncherCommand } from "./commands/get-cruncher";
import { DepositRewardCommand } from "./commands/deposit-reward";
import { EndCommand } from "./commands/end";
import { MarginCommand } from "./commands/margin";
import { DrainCommand } from "./commands/drain";
import { CheckpointCommand } from "./commands/checkpoint";
import { ListCrunchesCommand } from "./commands/list-crunches";
import packageJson from "../package.json";
import chalk from "chalk";

const program = new Command();

// Helper function to handle async command execution with proper exit codes
function handleAsyncCommand<T extends any[]>(commandFn: (...args: T) => Promise<void>) {
  return async (...args: T) => {
    try {
      await commandFn(...args);
    } catch (error) {
      const opts = program.opts();
      if (opts.output == "json") {
        const message = error instanceof Error ? error.message : String(error);
        console.log(JSON.stringify({ error: message }, null, 2));
      } else {
        const errorMessage = chalk.red(
          `⚠️  Unexpected error: ${error instanceof Error ? error.message : String(error)}`
        );
        console.log(errorMessage);
      }
      process.exit(1);
    }
  };
}

program
  .name("crunch-coordinator")
  .description("CrunchDAO Coordinator CLI")
  .version(packageJson.version)
  .option(
    "-u, --url <URL_OR_MONIKER>",
    "URL for Solana's JSON RPC or moniker (or their first letter): [mainnet-beta, testnet, devnet, localhost]"
  )
  .option("-m, --multisig <address>", "Squads multisig address for coordinator operations")
  .option("-w, --wallet <wallet>", "Path to wallet keypair")
  .option("-l, --loglevel <loglevel>", "Log level for program output")
  .option("-o, --output <format>", "Output format (json)")
  .option("-q, --quiet", "Quiet mode - minimal output")
  .option("-v, --verbose", "Verbose mode - detailed output");

program
  .command("register")
  .description("Register coordinator")
  .argument("<name>", "Coordinator name")
  .action(
    handleAsyncCommand(async (name) => {
      const opts = program.opts();
      const cmd = new RegisterCommand(opts);
      await cmd.execute(name);
    })
  );

program
  .command("get")
  .description("Get coordinator details")
  .argument("[owner]", "Owner address (optional, defaults to current wallet)")
  .action(
    handleAsyncCommand(async (owner) => {
      const opts = program.opts();
      const cmd = new GetCommand(opts);
      await cmd.execute(owner);
    })
  );

program
  .command("get-config")
  .description("Get coordinator config")
  .action(
    handleAsyncCommand(async () => {
      const opts = program.opts();
      const cmd = new GetConfigCommand(opts);
      await cmd.execute();
    })
  );

// Crunches management commands
const crunchesCommand = program.command("crunches").description("Crunches management");

crunchesCommand
  .command("list")
  .description("List all crunches for a coordinator")
  .argument("[wallet]", "Coordinator wallet address (optional, defaults to current wallet)")
  .action(
    handleAsyncCommand(async (wallet) => {
      const opts = program.opts();
      const cmd = new ListCrunchesCommand(opts);
      await cmd.execute(wallet);
    })
  );

// Crunch management commands
const crunchCommand = program.command("crunch").description("Crunch competition management");

crunchCommand
  .command("create")
  .description("Create a new crunch competition")
  .argument("<name>", "Name of the crunch")
  .argument("<payoutAmount>", "Payout amount in USDC")
  .argument("[maxModelsPerCruncher]", "Maximum models per cruncher", "2")
  .action(
    handleAsyncCommand(async (name, payoutAmount, maxModelsPerCruncher) => {
      const opts = program.opts();
      const cmd = new CreateCommand(opts);
      await cmd.execute(name, payoutAmount, maxModelsPerCruncher);
    })
  );

crunchCommand
  .command("start")
  .description("Start a crunch competition")
  .argument("<crunchName>", "Name of the crunch to start")
  .action(
    handleAsyncCommand(async (crunchName) => {
      const opts = program.opts();
      const cmd = new StartCommand(opts);
      await cmd.execute(crunchName);
    })
  );

crunchCommand
  .command("get")
  .description("Get crunch competition details")
  .argument("<name>", "Name of the crunch")
  .action(
    handleAsyncCommand(async (name) => {
      const opts = program.opts();
      const cmd = new GetCrunchCommand(opts);
      await cmd.execute(name);
    })
  );

crunchCommand
  .command("get-cruncher")
  .description("Get cruncher details for a specific crunch")
  .argument("<crunchName>", "Name of the crunch")
  .argument("<cruncherWallet>", "Cruncher wallet address (Base58)")
  .action(
    handleAsyncCommand(async (crunchName, cruncherWallet) => {
      const opts = program.opts();
      const cmd = new GetCruncherCommand(opts);
      await cmd.execute(crunchName, cruncherWallet);
    })
  );

crunchCommand
  .command("deposit-reward")
  .description("Deposit reward USDC to a crunch")
  .argument("<crunchName>", "Name of the crunch")
  .argument("<amount>", "Amount in USDC to deposit")
  .action(
    handleAsyncCommand(async (crunchName, amount) => {
      const opts = program.opts();
      const cmd = new DepositRewardCommand(opts);
      await cmd.deposit(crunchName, amount);
    })
  );

crunchCommand
  .command("end")
  .description("End a crunch competition")
  .argument("<crunchName>", "Name of the crunch to end")
  .action(
    handleAsyncCommand(async (crunchName) => {
      const opts = program.opts();
      const cmd = new EndCommand(opts);
      await cmd.execute(crunchName);
    })
  );

crunchCommand
  .command("margin")
  .description("Payout margin for a crunch")
  .argument("<crunchName>", "Name of the crunch")
  .action(
    handleAsyncCommand(async (crunchName) => {
      const opts = program.opts();
      const cmd = new MarginCommand(opts);
      await cmd.payout(crunchName);
    })
  );

crunchCommand
  .command("drain")
  .description("Drain USDC from a crunch")
  .argument("<crunchName>", "Name of the crunch")
  .option("--show-solscan", "Show Solscan links")
  .action(
    handleAsyncCommand(async (crunchName, options) => {
      const opts = program.opts();
      const cmd = new DrainCommand(opts);
      await cmd.execute(crunchName, opts.network, options.showSolscan);
    })
  );

crunchCommand
  .command("checkpoint-create")
  .description("Create a checkpoint for payout")
  .argument("<crunchName>", "Name of the crunch")
  .argument("<prizeFileName>", "Path to prize JSON file")
  .option("--dryrun", "Perform a dry run without executing")
  .action(
    handleAsyncCommand(async (crunchName, prizeFileName, options) => {
      const opts = program.opts();
      const cmd = new CheckpointCommand(opts);
      await cmd.create(crunchName, prizeFileName, options.dryrun);
    })
  );

crunchCommand
  .command("checkpoint-get-current")
  .description("Get current checkpoint")
  .argument("<crunchName>", "Name of the crunch")
  .action(
    handleAsyncCommand(async (crunchName) => {
      const opts = program.opts();
      const cmd = new CheckpointCommand(opts);
      await cmd.getCurrent(crunchName);
    })
  );

crunchCommand
  .command("checkpoint-get")
  .description("Get checkpoint by index")
  .argument("<crunchName>", "Name of the crunch")
  .argument("<checkpointIndex>", "Checkpoint index")
  .action(
    handleAsyncCommand(async (crunchName, checkpointIndex) => {
      const opts = program.opts();
      const cmd = new CheckpointCommand(opts);
      await cmd.getByIndex(crunchName, checkpointIndex);
    })
  );

crunchCommand
  .command("checkpoint-get-address")
  .description("Get checkpoint address")
  .argument("<crunchName>", "Name of the crunch")
  .argument("<checkpointIndex>", "Checkpoint index")
  .action(
    handleAsyncCommand(async (crunchName, checkpointIndex) => {
      const opts = program.opts();
      const cmd = new CheckpointCommand(opts);
      await cmd.getAddress(crunchName, checkpointIndex);
    })
  );

// Add shared config commands
addConfigCommands(program);

// Global error handlers
process.on("uncaughtException", (error) => {
  console.error("Uncaught exception:", error);
  process.exit(1);
});

process.on("unhandledRejection", (reason) => {
  console.error("Unhandled rejection:", reason);
  process.exit(1);
});

program.parse(process.argv);

if (!process.argv.slice(2).length) {
  program.outputHelp();
}
